import type { ComponentProps, HTMLAttributes } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import type { YouTubeProps } from 'react-youtube';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { OverflowProps, SizeProps, WidthProps } from 'styled-system';
import { overflow, size, space, width } from 'styled-system';

import type { ResponsiveImageWidths } from 'common/types/Image';
import type { SpaceProps } from '../../theme/system-types';
import type { CursorProps } from '../../theme/system-utilities';
import { cursor } from '../../theme/system-utilities';
import Facade from './Facade';
import type { SetVideoPlayer, YouTubePlayerRef } from './hooks';
import { useActiveEffect, useHandlers, YouTubePlayerState } from './hooks';
import LazyYoutube from './LazyYoutube';

interface YouTubeVideoProps extends HTMLAttributes<HTMLImageElement> {
  id: string;
  alt: string;
  isActive: boolean;
  isLazy: boolean;
  className?: string;
  setVideoPlayer?: SetVideoPlayer;
  widths?: ResponsiveImageWidths;
}

const playerVars = {
  autoplay: 1,
  fs: 1,
  iv_load_policy: 3,
  modestbranding: 1,
  rel: 0,
  enablejsapi: 1,
};

const Container = styled.div<
  {
    isOverlayed?: boolean;
  } & SizeProps &
    CursorProps &
    SpaceProps &
    OverflowProps &
    WidthProps
>`
  ${overflow};
  ${size};
  ${cursor};
  ${space};
  ${width};

  ${({ isOverlayed }) =>
    isOverlayed &&
    css`
      ::after {
        content: '';
        background: transparent;
        width: 100%;
        height: 100%;
        min-height: 1px;
        position: absolute;
        top: 0;
      }
    `};
`;

const containerStyles: ComponentProps<typeof Container> = {
  size: '100%',
  cursor: 'pointer',
  mx: 'auto',
  overflow: 'hidden',
};

const StyledLazyYoutube = styled(LazyYoutube)<SizeProps & WidthProps>`
  ${size};
  ${width};
`;

const lazyYoutubeStyles: ComponentProps<typeof StyledLazyYoutube> = {
  size: '100%',
};

const YouTubeVideo = ({
  id,
  isActive,
  isLazy,
  className,
  alt,
  setVideoPlayer,
  widths,
  ...props
}: YouTubeVideoProps) => {
  const currentPlayerRef = useRef<Nullable<YouTubePlayerRef>>(null);
  const [isPreloaded, setIsPreloaded] = useState(false);
  const [isOverlayed, setOverlayed] = useState(true);
  const options = {
    playerVars,
    width: '100%',
    height: '100%',
  };
  const { onClick, onKeyDown, ...handlers } = useHandlers({
    isPreloaded,
    setIsPreloaded,
    setOverlayed,
  });

  useActiveEffect(isActive, handlers.onPause);

  const onPlayerReady: YouTubeProps['onReady'] = event => {
    currentPlayerRef.current = event.target;
  };

  const isVideoPlaying = () =>
    currentPlayerRef.current?.getPlayerState() === YouTubePlayerState.Playing;

  const handleVideoPlayPause = () => {
    return () => {
      setIsPreloaded(true);
      if (isVideoPlaying()) {
        currentPlayerRef.current?.pauseVideo();
      } else {
        currentPlayerRef.current?.playVideo();
      }
    };
  };

  useEffect(() => {
    if (isActive) {
      setVideoPlayer?.(handleVideoPlayPause);
    } else {
      if (isVideoPlaying()) {
        currentPlayerRef.current?.pauseVideo();
      }
      setVideoPlayer?.(null);
    }
  }, [isActive]);

  return (
    <Container
      {...containerStyles}
      onClick={onClick}
      onKeyDown={onKeyDown}
      isOverlayed={isOverlayed}
      className={className}
      data-testid="youtube-video-container"
      {...(widths ? { width: widths } : {})}
    >
      {isPreloaded ? (
        <StyledLazyYoutube
          {...handlers}
          {...lazyYoutubeStyles}
          videoId={id}
          opts={options}
          loading="lazy"
          onReady={onPlayerReady}
        />
      ) : (
        <Facade {...props} alt={alt} videoId={id} isLazy={isLazy} />
      )}
    </Container>
  );
};

export default YouTubeVideo;
